"use client";

import { useEffect, useRef, useCallback } from "react";
import { getPaymentStatus } from "@/lib/api/checkout";
import type { SessionStatus } from "@/lib/api/checkout.schema";

const POLLING_INTERVAL = 3000; // 3 seconds

export interface PaymentPollingCallbacks {
  onCompleted?: (data: { redirect_url?: string | null }) => void;
  onFailed?: (data: { redirect_url?: string | null }) => void;
  onCancelled?: (data: { redirect_url?: string | null }) => void;
  onExpired?: (data: { redirect_url?: string | null }) => void;
  onError?: (error: string) => void;
}

interface UsePaymentPollingOptions {
  token: string;
  enabled?: boolean;
  callbacks: PaymentPollingCallbacks;
}

export function usePaymentPolling({
  token,
  enabled = false,
  callbacks,
}: UsePaymentPollingOptions) {
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const callbacksRef = useRef(callbacks);

  // Keep callbacks ref updated
  useEffect(() => {
    callbacksRef.current = callbacks;
  });

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    try {
      const result = await getPaymentStatus(token);

      if (result.error) {
        // Continue polling on errors - the payment may still complete
        return;
      }

      const data = result.data;
      if (!data) return;

      const status: SessionStatus = data.status;

      switch (status) {
        case "completed":
          stopPolling();
          callbacksRef.current.onCompleted?.({
            redirect_url: data.redirect_url,
          });
          break;

        case "failed":
          stopPolling();
          callbacksRef.current.onFailed?.({
            redirect_url: data.redirect_url,
          });
          break;

        case "cancelled":
          stopPolling();
          callbacksRef.current.onCancelled?.({
            redirect_url: data.redirect_url,
          });
          break;

        case "expired":
          stopPolling();
          callbacksRef.current.onExpired?.({
            redirect_url: data.redirect_url,
          });
          break;

        case "pending":
        case "active":
          // Keep polling
          break;
      }
    } catch {
      // Continue polling on network errors - the payment may still complete
    }
  }, [token, stopPolling]);

  const startPolling = useCallback(() => {
    stopPolling();
    // Poll immediately, then set interval
    poll();
    pollingIntervalRef.current = setInterval(poll, POLLING_INTERVAL);
  }, [poll, stopPolling]);

  useEffect(() => {
    if (enabled) {
      startPolling();
    } else {
      stopPolling();
    }

    return stopPolling;
  }, [enabled, startPolling, stopPolling]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return { startPolling, stopPolling };
}
